import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const navItems = [
    { id: 'home', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'services', label: t.nav.services },
    { id: 'gallery', label: t.nav.gallery },
    { id: 'booking', label: t.nav.booking },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => scrollToSection('home')}
          className="flex items-center group"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_nailsbymoncton/artifacts/lvf4tsch_a-removebg-preview.png"
            alt="AB Nail Lounge"
            className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-sm text-stone-600 hover:text-rose-400 transition-colors duration-300 tracking-wide uppercase font-light relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-rose-400 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        {/* Language Toggle & Mobile Menu */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleLanguage}
            className="text-sm font-medium text-stone-600 hover:text-rose-400 transition-colors duration-300 px-3 py-1.5 border border-stone-200 rounded-full hover:border-rose-300"
          >
            {language === 'fr' ? 'EN' : 'FR'}
          </button>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-stone-600">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white/95 backdrop-blur-lg w-80">
              <div className="flex flex-col gap-8 pt-12">
                <div className="text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_nailsbymoncton/artifacts/lvf4tsch_a-removebg-preview.png"
                    alt="AB Nail Lounge"
                    className="h-20 w-auto mx-auto object-contain"
                  />
                </div>
                <nav className="flex flex-col gap-4">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="text-lg text-stone-600 hover:text-rose-400 transition-colors duration-300 tracking-wide py-3 border-b border-stone-100"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
